import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { paymentsAPI } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Download, RotateCcw, DollarSign, FileX, AlertCircle } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const fmt = (n) => (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

const RefundLog = () => {
  const { toast } = useToast();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await paymentsAPI.getRefundLog();
      setEntries(data);
    } catch {
      toast({ title: 'Error', description: 'Failed to load refund log', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const totalRefunded = entries.filter(e => e.original_payment_status === 'paid').reduce((s, e) => s + (e.amount || 0), 0);
  const totalCancelled = entries.length;

  const exportCSV = () => {
    if (entries.length === 0) return;
    const headers = ['Order ID', 'Customer', 'Email', 'Amount', 'Reason', 'Cancelled At', 'Cancelled By', 'Payment Status', 'Refund ID'];
    const rows = entries.map(e => [e.order_id, e.customer_name, e.customer_email, e.amount, `"${(e.cancellation_reason || '').replace(/"/g, '""')}"`, e.cancelled_at, e.cancelled_by, e.payment_status, e.refund_id]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'refund-audit-log.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="refund-log-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800" data-testid="refund-log-title">Refund & Cancellation Log</h1>
            <p className="text-sm text-slate-500 mt-1">Audit trail of all order cancellations and refunds</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={load}><RotateCcw className="h-4 w-4 mr-1" /> Refresh</Button>
            <Button variant="outline" onClick={exportCSV} data-testid="export-refund-log"><Download className="h-4 w-4 mr-1" /> Export CSV</Button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="rounded-xl border p-4 bg-red-50 text-red-700 border-red-200" data-testid="total-cancelled">
            <div className="flex items-center gap-2 mb-1"><FileX className="h-4 w-4 opacity-70" /><span className="text-xs font-semibold opacity-70">Total Cancelled</span></div>
            <p className="text-xl font-bold">{totalCancelled}</p>
          </div>
          <div className="rounded-xl border p-4 bg-amber-50 text-amber-700 border-amber-200" data-testid="total-refunded">
            <div className="flex items-center gap-2 mb-1"><DollarSign className="h-4 w-4 opacity-70" /><span className="text-xs font-semibold opacity-70">Total Refunded</span></div>
            <p className="text-xl font-bold">${fmt(totalRefunded)}</p>
          </div>
          <div className="rounded-xl border p-4 bg-sky-50 text-sky-700 border-sky-200" data-testid="with-payment-refund">
            <div className="flex items-center gap-2 mb-1"><RotateCcw className="h-4 w-4 opacity-70" /><span className="text-xs font-semibold opacity-70">Processed Refunds</span></div>
            <p className="text-xl font-bold">{entries.filter(e => e.refund_id).length}</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-40"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600" /></div>
        ) : (
          <Card className="border shadow-sm">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="refund-table">
                  <thead>
                    <tr className="bg-slate-50 border-b">
                      <th className="text-left py-3 px-4 text-xs font-bold text-slate-500 uppercase">Order ID</th>
                      <th className="text-left py-3 px-4 text-xs font-bold text-slate-500 uppercase">Customer</th>
                      <th className="text-right py-3 px-4 text-xs font-bold text-slate-500 uppercase">Amount</th>
                      <th className="text-left py-3 px-4 text-xs font-bold text-slate-500 uppercase">Reason</th>
                      <th className="text-center py-3 px-4 text-xs font-bold text-slate-500 uppercase">Refund</th>
                      <th className="text-left py-3 px-4 text-xs font-bold text-slate-500 uppercase">Cancelled By</th>
                      <th className="text-left py-3 px-4 text-xs font-bold text-slate-500 uppercase">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {entries.length === 0 ? (
                      <tr><td colSpan={7} className="py-12 text-center text-slate-400">No cancellations or refunds</td></tr>
                    ) : entries.map((e) => (
                      <tr key={e.order_id} className="hover:bg-slate-50 transition-colors" data-testid={`refund-row-${e.order_id}`}>
                        <td className="py-3 px-4 font-mono text-sm font-medium text-sky-700">{e.order_id}</td>
                        <td className="py-3 px-4">
                          <p className="text-sm font-medium text-slate-800">{e.customer_name || '-'}</p>
                          <p className="text-xs text-slate-400">{e.customer_email || ''}</p>
                        </td>
                        <td className="py-3 px-4 text-right text-sm font-bold text-slate-800">${fmt(e.amount)}</td>
                        <td className="py-3 px-4 text-sm text-slate-600 max-w-xs truncate">{e.cancellation_reason || '-'}</td>
                        <td className="py-3 px-4 text-center">
                          {e.refund_id ? (
                            <Badge className="bg-green-100 text-green-700">Refunded</Badge>
                          ) : e.original_payment_status === 'paid' ? (
                            <Badge className="bg-amber-100 text-amber-700">Pending</Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-500">N/A</Badge>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-500">{e.cancelled_by || '-'}</td>
                        <td className="py-3 px-4 text-xs text-slate-400">{fmtDate(e.cancelled_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default RefundLog;
